import React, { useCallback } from 'react';
import {
  Box, TextField, IconButton, Button, Typography, Tooltip, useTheme,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

let nextId = Date.now();
const uid = () => ++nextId;

function fmt(n) {
  return Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * LineItemsTable — ตารางรายการสินค้า/บริการ
 * ใช้ React.memo เพื่อป้องกัน re-render ที่ไม่จำเป็น (แก้ focus-loss bug)
 */
const LineItemsTable = React.memo(function LineItemsTable({ items = [], onChange, errors, disabled }) {
  const theme = useTheme();

  const update = useCallback((id, field, value) => {
    onChange(
      items.map((it) => {
        if (it._id !== id && it.id !== id) return it;
        const next = { ...it, [field]: value };
        if (field === 'quantity' || field === 'unit_price') {
          next.amount = Math.round((Number(next.quantity) || 0) * (Number(next.unit_price) || 0) * 100) / 100;
        }
        return next;
      }),
    );
  }, [items, onChange]);

  const addRow = useCallback(() => {
    onChange([...items, { id: uid(), description: '', description_detail: '', quantity: 1, unit: 'JOB', unit_price: 0, amount: 0 }]);
  }, [items, onChange]);

  const removeRow = useCallback((id) => {
    onChange(items.filter((it) => (it._id || it.id) !== id));
  }, [items, onChange]);

  const cellH = 40;

  return (
    <Box>
      <Box sx={{ borderRadius: 2, overflowX: 'auto', border: `1px solid ${theme.palette.divider}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: 44 }} />
            <col />
            <col style={{ width: 90 }} />
            <col style={{ width: 80 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 130 }} />
            <col style={{ width: 44 }} />
          </colgroup>
          <thead>
            <tr style={{ background: theme.palette.primary.main, color: '#fff' }}>
              {['#', 'รายละเอียด / Description', 'จำนวน', 'หน่วย', 'ราคา/หน่วย', 'ยอดรวม', ''].map((h) => (
                <th key={h} style={{ padding: '10px 8px', textAlign: h === 'ยอดรวม' ? 'right' : 'center', fontSize: 13, fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              // key ต้องเสถียร — ใช้ _id (จาก DB) หรือ id (frontend uid) เสมอ ห้าม fallback เป็น index
              const id = item._id || item.id;
              const rowBg = index % 2 === 0 ? '#fff' : '#f8fafc';
              return (
                <React.Fragment key={String(id)}>
                  <tr style={{ background: rowBg, verticalAlign: 'top' }}>
                    <td style={{ textAlign: 'center', padding: '10px 4px', fontWeight: 500 }}>{index + 1}</td>
                    <td style={{ padding: '8px 4px' }}>
                      <TextField
                        fullWidth
                        value={item.description}
                        onChange={(e) => update(id, 'description', e.target.value)}
                        placeholder="ชื่อรายการ / Description"
                        size="small"
                        disabled={disabled}
                        error={!!errors?.items && !item.description}
                        helperText={errors?.items && !item.description ? 'กรุณาระบุ' : ''}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, height: cellH } }}
                      />
                    </td>
                    <td style={{ padding: '8px 2px' }}>
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => update(id, 'quantity', parseFloat(e.target.value) || 0)}
                        size="small"
                        disabled={disabled}
                        inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 2, height: cellH } }}
                      />
                    </td>
                    <td style={{ padding: '8px 2px' }}>
                      <TextField
                        value={item.unit}
                        onChange={(e) => update(id, 'unit', e.target.value)}
                        placeholder="JOB"
                        size="small"
                        disabled={disabled}
                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 2, height: cellH } }}
                      />
                    </td>
                    <td style={{ padding: '8px 4px' }}>
                      <TextField
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => update(id, 'unit_price', parseFloat(e.target.value) || 0)}
                        size="small"
                        disabled={disabled}
                        inputProps={{ min: 0, step: 'any', style: { textAlign: 'right' } }}
                        sx={{ width: '100%', '& .MuiOutlinedInput-root': { borderRadius: 2, height: cellH } }}
                      />
                    </td>
                    <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 600, paddingTop: 18 }}>
                      {fmt(item.amount ?? (item.quantity * item.unit_price))}
                    </td>
                    <td style={{ textAlign: 'center', padding: '8px 2px' }}>
                      {items.length > 1 && !disabled && (
                        <Tooltip title="ลบรายการ">
                          <IconButton size="small" color="error" onClick={() => removeRow(id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </td>
                  </tr>
                  <tr style={{ background: rowBg, verticalAlign: 'top' }}>
                    <td />
                    <td colSpan={6} style={{ padding: '0 4px 10px' }}>
                      <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        value={item.description_detail || ''}
                        onChange={(e) => update(id, 'description_detail', e.target.value)}
                        placeholder="รายละเอียดเพิ่มเติม (ถ้ามี)"
                        size="small"
                        disabled={disabled}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 }, '& textarea': { resize: 'none' } }}
                      />
                    </td>
                  </tr>
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </Box>
      {errors?.items && (
        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{errors.items}</Typography>
      )}
      {!disabled && (
        <Box sx={{ mt: 2 }}>
          <Button startIcon={<AddIcon />} variant="outlined" size="small" onClick={addRow} sx={{ borderRadius: 2 }}>
            เพิ่มรายการ
          </Button>
        </Box>
      )}
    </Box>
  );
});

export default LineItemsTable;
